import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, BadgeCheck, Languages, ReceiptText, ScrollText, ChevronRight, ChevronLeft } from "lucide-react";
import { track } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Container, ProductFrame, Reducers, Reveal, SmartLink, useCopy } from "./shared";

/* ----------------------------------- hero ---------------------------------- */

export function Hero() {
  const { c, ar } = useCopy();
  const Arrow = ar ? ArrowLeft : ArrowRight;
  return (
    <section id="top" className="lp-shell relative isolate overflow-hidden" data-testid="landing-hero">
      <div className="lp-hero-wash absolute inset-x-0 top-0 -z-10" aria-hidden />
      <div className="noise-overlay absolute inset-0 -z-10 opacity-50" aria-hidden />
      <Container className="grid grid-cols-1 items-center gap-10 pb-12 pt-12 sm:pt-16 lg:grid-cols-[0.95fr_1.05fr] lg:gap-12 lg:pb-16 lg:pt-20">
        <div>
          <Reveal><Badge variant="outline" className="rounded-full border-primary/30 bg-card/70 px-3 py-1 text-2xs font-semibold uppercase tracking-[0.14em] text-primary backdrop-blur">{c.hero.eyebrow}</Badge></Reveal>
          <Reveal delay={0.05}><h1 className="lp-display mt-5 text-4xl font-semibold leading-[1.1] tracking-[-0.02em] sm:text-5xl lg:text-[3.4rem]" data-testid="landing-hero-title">{c.hero.title}</h1></Reveal>
          <Reveal delay={0.1}><p className="lp-body mt-5 max-w-xl text-base leading-7 text-muted-foreground sm:text-lg sm:leading-8" data-testid="landing-hero-subtitle">{c.hero.subtitle}</p></Reveal>
          <Reveal delay={0.15}>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button asChild size="lg" className="h-12 rounded-xl px-6 shadow-[var(--shadow-sm)] transition-shadow duration-150 hover:shadow-[var(--shadow-md)]" data-testid="landing-hero-create-account-button">
                <Link to="/auth?mode=register" onClick={() => track("landing_cta_click", { placement: "hero" })}>{c.hero.primary}<Arrow size={16} className="ms-2" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="h-12 rounded-xl bg-card/70 px-6 backdrop-blur" data-testid="landing-hero-model-holding-button">
                <Link to="/model" onClick={() => track("landing_secondary_click", { placement: "hero" })}>{c.hero.secondary}</Link>
              </Button>
            </div>
            <Reducers items={c.hero.reducers} className="mt-4" testId="landing-hero-reducers" />
          </Reveal>
        </div>
        <Reveal delay={0.12}>
          <ProductFrame screen="holding" alt={c.hero.frameAlt} caption={c.hero.frameCaption} badge={c.hero.frameBadge} priority className="lp-frame-hero" />
        </Reveal>
      </Container>
      <ProofBand />
    </section>
  );
}

/* -------------------------------- proof band ------------------------------- */

const PROOF_ICONS = { record: ScrollText, taqeem: BadgeCheck, fees: ReceiptText, bilingual: Languages };

/** Four claims the visitor can check (committee R-6): each links to the page that backs it, or stands on its own if self-evident. */
export function ProofBand() {
  const { c, ar } = useCopy();
  const Chevron = ar ? ChevronLeft : ChevronRight;
  return (
    <div className="border-t border-border bg-card/60 backdrop-blur-sm" data-testid="landing-proof-band">
      <Container className="py-5 sm:py-6">
        <p className="lp-eyebrow mb-3 text-muted-foreground">{c.proof.label}</p>
        <ul className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
          {c.proof.items.map((item) => {
            const I = PROOF_ICONS[item.k] || BadgeCheck;
            const linked = Boolean(item.to || item.href);
            return (
              <li key={item.k}>
                <SmartLink to={item.to} href={item.href} className={linked ? "lp-focus group flex items-start gap-3 rounded-lg -m-1 p-1 transition-colors hover:bg-accent/50" : "flex items-start gap-3 p-0"} data-testid={`landing-proof-${item.k}`}>
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground"><I size={15} strokeWidth={1.75} /></span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold leading-snug">{item.label}{linked && <Chevron size={12} className="ms-1 inline-block text-muted-foreground transition-transform duration-150 group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5" aria-hidden />}</span>
                    <span className="block text-xs leading-relaxed text-muted-foreground">{item.detail}</span>
                  </span>
                </SmartLink>
              </li>
            );
          })}
        </ul>
      </Container>
    </div>
  );
}
